import React, { useState, useRef, useEffect } from 'react';
import { DB } from '../data/database';

const FILTER_KEY = 'bigstar_customer_filter';

// Get persisted customer filter
export function getPersistedFilter() {
  try { return localStorage.getItem(FILTER_KEY) || ''; } catch { return ''; }
}

// Set persisted customer filter
export function setPersistedFilter(id) {
  try { if (id) localStorage.setItem(FILTER_KEY, id); else localStorage.removeItem(FILTER_KEY); } catch {}
}

// ── Customer Filter Dropdown ──────────────────────────────────
function CustomerFilter({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const customers = DB.get('customers') || [];
  const sorted = [...customers].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const filtered = sorted.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q) || (c.country || '').toLowerCase().includes(q);
  });

  const selected = customers.find(c => c.id === value);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (id) => {
    onChange(id);
    setPersistedFilter(id);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn btn-secondary btn-sm"
        onClick={() => setOpen(!open)}
        style={{
          minWidth: 160, textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          ...(value ? { borderColor: 'var(--blue)', color: 'var(--blue)', background: 'rgba(88,166,255,0.08)' } : {}),
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.name : 'All Customers'}
        </span>
        <span style={{ fontSize: 10, flexShrink: 0 }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 8px 30px rgba(0,0,0,0.4)', minWidth: 260, maxHeight: 360, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Search */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
            <input
              className="form-input"
              placeholder="Search customers…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
              style={{ fontSize: 12, padding: '6px 10px' }}
            />
          </div>

          {/* Options */}
          <div style={{ overflowY: 'auto', maxHeight: 280 }}>
            {/* All Customers option */}
            <div
              onClick={() => select('')}
              style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: 13,
                background: !value ? 'rgba(88,166,255,0.1)' : 'transparent',
                fontWeight: !value ? 700 : 400,
                borderBottom: '1px solid var(--border)',
              }}
            >
              All Customers
            </div>

            {filtered.map(c => (
              <div
                key={c.id}
                onClick={() => select(c.id)}
                style={{
                  padding: '8px 14px', cursor: 'pointer', fontSize: 12,
                  background: value === c.id ? 'rgba(88,166,255,0.1)' : 'transparent',
                  fontWeight: value === c.id ? 700 : 400,
                  borderBottom: '1px solid var(--border)',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (value !== c.id) e.currentTarget.style.background = 'var(--bg3)'; }}
                onMouseLeave={e => { if (value !== c.id) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                  {c.company}{c.country ? ` · ${c.country}` : ''}
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div style={{ padding: '16px 14px', color: 'var(--text3)', fontSize: 12, textAlign: 'center' }}>
                No customers match
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Active Filter Banner ──────────────────────────────────────
export function CustomerFilterBanner({ value, onChange }) {
  const customers = DB.get('customers') || [];
  const selected = customers.find(c => c.id === value);
  if (!selected) return null;

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '8px 16px', marginBottom: 14,
      background: 'rgba(88,166,255,0.08)', border: '1px solid rgba(88,166,255,0.2)',
      borderRadius: 8, fontSize: 13,
    }}>
      <span>
        <span style={{ color: 'var(--text3)' }}>Viewing: </span>
        <strong style={{ color: 'var(--blue)' }}>{selected.name}</strong>
        {selected.company && <span style={{ color: 'var(--text3)' }}> — {selected.company}</span>}
      </span>
      <button
        onClick={() => { onChange(''); setPersistedFilter(''); }}
        style={{
          background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer',
          fontSize: 12, padding: '4px 8px', borderRadius: 4,
        }}
      >
        ✕ Clear Filter
      </button>
    </div>
  );
}

export default CustomerFilter;
